(() => {
  if (window.miaSoftFocusReady) return;
  const root = document.documentElement;
  if (getComputedStyle(root).getPropertyValue('--mia-soft-focus-css').trim() !== '1') return;
  window.miaSoftFocusReady = true;
  const records = new WeakMap();
  const fields = 'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="submit"]):not([type="button"]), textarea, select';
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const canBlendBorder = CSS.supports('border-color', 'color-mix(in srgb, black 50%, white)');
  const borderSides = ['top', 'right', 'bottom', 'left'];
  let current;
  const indicatorTarget = (element) => element.closest('.quantity-stepper, .collection-sort, .mobile-country-selector') || (element.matches('.reference-categories__region') ? element.querySelector('.reference-categories__name-row') : element);
  const fade = (record, destination) => {
    cancelAnimationFrame(record.fadeFrame);
    const from = record.alpha;
    const started = performance.now();
    const duration = reducedMotion.matches ? 0 : 420;
    const paint = (time) => {
      const progress = duration ? Math.max(0, Math.min(1, (time - started) / duration)) : 1;
      const eased = progress * progress * (3 - 2 * progress);
      record.alpha = from + (destination - from) * eased;
      record.target.style.setProperty('--mia-soft-focus-alpha', String(record.alpha));
      if (progress < 1) record.fadeFrame = requestAnimationFrame(paint);
      else if (destination === 0) record.target.removeAttribute('data-mia-focus-border');
    };
    if (duration) record.fadeFrame = requestAnimationFrame(paint);
    else paint(started);
  };
  const sync = () => {
    const element = document.activeElement;
    const field = element instanceof HTMLElement && (element.matches(fields) || !!element.closest('.quantity-stepper'));
    const keyboard = root.dataset.miaInputModality !== 'pointer';
    const eligible = element instanceof HTMLElement && !element.matches('[tabindex="-1"]') && (field || (keyboard && element.matches(':focus-visible')));
    const target = eligible ? indicatorTarget(element) : null;
    if (current?.target === target) return;
    if (current) {
      current.target.classList.remove('is-mia-focus-visible');
      fade(current, 0);
      current = null;
    }
    if (!target || !target.getClientRects().length) return;
    let record = records.get(target);
    if (!record) {
      // Paint on the control itself, so scrolling and the iOS keyboard cannot detach the outline.
      target.style.setProperty('--mia-soft-focus-offset', field ? '0px' : '2px');
      target.dataset.miaFocusTone = element.closest('.ocean-garden-hero, .reference-hero, .section--dark') ? 'light' : 'neutral';
      target.classList.add('mia-soft-focus-target');
      record = { target, alpha: 0 };
      records.set(target, record);
    }
    // Bordered fields use their existing edge, not a second outline with different corner geometry.
    target.removeAttribute('data-mia-focus-border');
    if (field && canBlendBorder) {
      const style = getComputedStyle(target);
      if (borderSides.every((side) => parseFloat(style.getPropertyValue(`border-${side}-width`)) > 0 && style.getPropertyValue(`border-${side}-style`) === 'solid')) {
        borderSides.forEach((side) => target.style.setProperty(`--mia-soft-focus-border-${side}`, style.getPropertyValue(`border-${side}-color`)));
        target.setAttribute('data-mia-focus-border', '');
      }
    }
    current = record;
    target.classList.add('is-mia-focus-visible');
    fade(record, 1);
  };
  root.setAttribute('data-mia-focus-ready', '');
  document.addEventListener('focusin', sync);
  document.addEventListener('focusout', () => queueMicrotask(sync));
  // WebKit may omit focusout when an AJAX update moves the focused control.
  new MutationObserver((changes) => {
    const target = current?.target;
    if (target && changes.some((change) => Array.from(change.removedNodes).some((node) => node.contains(target)))) sync();
  }).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('pointerdown', () => {
    root.dataset.miaInputModality = 'pointer';
    sync();
  }, true);
  document.addEventListener('keydown', (event) => {
    if (!['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) {
      root.dataset.miaInputModality = 'keyboard';
      sync();
    }
  }, true);
  window.addEventListener('pageshow', sync);
  sync();
})();

(() => {
  const viewport = window.visualViewport;
  if (!viewport || window.miaFieldVisibilityReady) return;
  window.miaFieldVisibilityReady = true;
  const mobile = matchMedia('(max-width: 760px)');
  const nonTextTypes = new Set(['hidden', 'checkbox', 'radio', 'submit', 'button', 'reset', 'range', 'color', 'file', 'image']);
  let pending;
  let timer;
  const cancel = () => { clearTimeout(timer); pending = null; };
  const reveal = () => {
    const attempt = pending;
    if (!attempt || performance.now() > attempt.until || document.activeElement !== attempt.field || !attempt.field.isConnected) {
      cancel();
      return;
    }
    // Keyboard panning changes the visual viewport, while element rectangles use the layout viewport.
    // Leave native zoom, locked overlays, and ordinary browser toolbar resizing alone.
    if (!mobile.matches || Math.abs(viewport.scale - 1) > .05 || document.documentElement.clientHeight - viewport.height < 120 || document.querySelector('dialog[open]') || document.body.classList.contains('menu-open')) return;
    const top = viewport.offsetTop + 24;
    const bottom = viewport.offsetTop + viewport.height - (attempt.promo ? 112 : attempt.newsletter ? 96 : 32);
    const formRect = attempt.promo?.getBoundingClientRect();
    const rect = formRect && formRect.height <= bottom - top ? formRect : attempt.field.getBoundingClientRect();
    const delta = rect.height > bottom - top ? rect.top - top : rect.bottom > bottom ? rect.bottom - bottom : rect.top < top ? rect.top - top : 0;
    if (Math.abs(delta) <= 1 || !attempt.field.getClientRects().length) return;
    // Correct only after Safari's keyboard animation settles; do not start a competing smooth scroll.
    window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
    if (++attempt.corrections >= (attempt.newsletter ? 1 : 3)) cancel();
  };
  const schedule = () => {
    clearTimeout(timer);
    if (pending && performance.now() <= pending.until) timer = setTimeout(reveal, pending.newsletter ? 240 : 180);
  };
  document.addEventListener('focusin', (event) => {
    cancel();
    const field = event.target;
    if (!mobile.matches || !(field instanceof HTMLTextAreaElement || field instanceof HTMLInputElement && !nonTextTypes.has(field.type)) || field.readOnly || field.disabled || field.closest('dialog')) return;
    // A short opening window handles delayed iOS panning without snapping back later in the visit.
    pending = { field, until: performance.now() + 2400, corrections: 0, newsletter: !!field.closest('.mia-newsletter__form'), promo: field.closest('.cart-promo__form') };
    schedule();
  });
  document.addEventListener('focusout', cancel);
  document.addEventListener('touchmove', cancel, { passive: true });
  document.addEventListener('wheel', cancel, { passive: true });
  document.addEventListener('pointerdown', cancel, { passive: true });
  viewport.addEventListener('resize', schedule, { passive: true });
  viewport.addEventListener('scroll', schedule, { passive: true });
})();
